/**
 * Which practice-scoped surfaces a member may reach, derived from the capability set
 * the server resolves per membership (`/me` → `memberships[].capabilities`).
 *
 * ONE definition, two consumers: the primary nav (which decides whether to offer a
 * screen) and PracticeOverviewScreen (which decides whether to render that screen's
 * card and fire its query). They were identical copies for one commit; a shared module
 * is what stops them drifting apart again, which is the whole point of rebuild plan
 * §5.4 — the nav previously re-derived its own rules from the role and was wrong about
 * Templates for a year.
 *
 * These are display predicates only. The server is the authority: every screen behind
 * them re-checks on each request. Widening one here grants nothing.
 */

/** Screens are composites, so each predicate is "ANY section of the screen is reachable". */
export type PracticeSurfaceAccess = {
  /** BillingProfileSection (back-office) + SessionsAwaitingBilling (owner/admin). */
  readonly canSeeBilling: boolean;
  /** Contract templates (back-office) + document templates (owner-only). */
  readonly canSeeTemplates: boolean;
  /** The staff management list — owner and admin only (decision D-6). */
  readonly canSeeTeam: boolean;
  /** Practice settings, DPA, governance. */
  readonly canGovern: boolean;
  /** The compliance surface — audit log, subject requests, breach register. */
  readonly canSeeCompliance: boolean;
  /** Plan & usage — what the practice pays HapMetrix, and the capacity it holds. */
  readonly canSeePlan: boolean;
  readonly canQuickAdd: boolean;
  /** This practice's cases: the business directory, GET /practices/:id/cases. */
  readonly canSeeCases: boolean;
  /** The ABA target library — a clinician's tool, but a practice's data. */
  readonly canSeeLibrary: boolean;
  /** The enquiry pipeline (ADR 0004 front-office; owner implicit). Business lens — it moved
   *  behind an explicit practice in slice D, because the unscoped screen merged every
   *  front-office practice and a per-practice figure opened an all-practice page. */
  readonly canSeeEnquiries: boolean;
};

/**
 * A MEMBERSHIP, not a capability array. `enquiry.list` is gated on front-office authority
 * AND an accepted DPA (permission.ts, the `enquiry.list`/`enquiry.create` case), and an
 * owner holds `practice.front_office` implicitly from the moment the practice exists — so
 * a capabilities-only predicate offered a brand-new owner the Enquiries screen and the
 * server refused every request it made. Taking the whole membership means the next
 * precondition is added here, once, instead of at three call sites.
 */
export type PracticeAccessInput = {
  readonly capabilities?: readonly string[] | undefined;
  readonly dpaAccepted?: boolean | undefined;
};

/**
 * The server action each surface's PRIMARY read performs — the one that decides whether the
 * screen has anything on it at all. Declared here so a gate can compare each predicate
 * against the preconditions that action really carries, instead of trusting that whoever
 * added the surface read permission.ts. Adding a key to `PracticeSurfaceAccess` without
 * adding it here fails tests/architecture/practice-surface-preconditions.test.ts.
 *
 * Not "every action the screen can perform": a screen offers many, and the nav question is
 * only ever "is there anything here for me".
 */
export const SURFACE_PRIMARY_ACTION: Readonly<Record<keyof PracticeSurfaceAccess, string>> = {
  canSeeBilling: 'practice.billing_profile_update',
  canSeeTemplates: 'contract_template.list',
  canSeeTeam: 'practice.members_list',
  canGovern: 'practice.read',
  // Its own row, because `canGovern` gates TWO rail items whose primary reads differ:
  // Settings reads `practice.read` (any active member), Compliance reads `audit_log.read`
  // (governance only). One mapping could only be right about one of them, so a precondition
  // added to `audit_log.read` alone would have gone unmirrored (2026-08-19).
  canSeeCompliance: 'audit_log.read',
  canSeePlan: 'subscription.plan_read',
  canSeeEnquiries: 'enquiry.list',
  canSeeLibrary: 'target_library.list',
  canSeeCases: 'case.view_business',
  canQuickAdd: 'case.quick_add',
};

/**
 * The front-office half of what `enquiry.create` requires. Named and exported because the
 * enquiry wizard asks it about the practice its ROUTE names, and reports the DPA half
 * separately with a different refusal — so it cannot reuse `canSeeEnquiries`, which is both
 * halves at once. A named pure function is also the only form a gate can EVALUATE: as an
 * expression inlined in the component it could only be pattern-matched, and review defeated
 * two successive pattern-matchers on exactly this guard.
 */
export function holdsFrontOffice(membership: PracticeAccessInput | null | undefined): boolean {
  return (membership?.capabilities ?? []).includes('practice.front_office');
}

export function practiceSurfaceAccess(
  membership: PracticeAccessInput | null | undefined,
): PracticeSurfaceAccess {
  const holds = (c: string): boolean => (membership?.capabilities ?? []).includes(c);
  return {
    canSeeBilling: holds('practice.back_office') || holds('case.review_session_charge'),
    canSeeTemplates: holds('practice.back_office') || holds('practice.govern'),
    canSeeTeam: holds('practice.view_members'),
    // Deliberately NOT DPA-gated: governance is where the DPA is accepted, so gating it on
    // the DPA would lock a new owner out of the only screen that can unlock the rest.
    canGovern: holds('practice.govern'),
    canSeeCompliance: holds('practice.govern'),
    // Back-office authority, owner implicit — the same grant `subscription.plan_read` gates
    // on (permission.ts) and the same one ProtectedLayout's planLinkFor already reads.
    canSeePlan: holds('practice.back_office'),
    canSeeEnquiries: holdsFrontOffice(membership) && membership?.dpaAccepted === true,
    // NOT DPA-gated: target_library.* carries no DPA precondition server-side.
    canSeeLibrary: holds('target_library.manage'),
    canSeeCases: holds('case.view_business'),
    canQuickAdd: holds('case.create') && membership?.dpaAccepted === true,
  };
}
